<script lang="ts">
  import type { App } from '$lib/types';

  import { getContext, createEventDispatcher } from 'svelte';
  import { page } from '$app/stores';
  import { Toasts } from '$lib/stores';
  import { BASE_APP_URL, STATIC_URL } from '$lib/constants';
  import { analytics, fetch } from '$lib/utils';

  import FormControl from '$components/FormControl.svelte';
  import OAuthButtons from './OAuthButtons.svelte';
  import SpaceManAnimation from './illustrations/SpaceManAnimation.svelte';
  import Button from '$mds/Buttons/Button';
  import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
  import { createPinInput } from '@melt-ui/svelte';

  import { doWork, authenticateWithAPI } from '$routes/auth/external/[...provider]/+page.svelte';

  let className = '';
  export { className as class };
  export let variant: 'full' | 'contained' = 'full';
  export let switchHref = false;

  const { irkAccount, refreshSession } = getContext<App>('app');
  const dispatch = createEventDispatcher();

  const {
    elements: { root, input },
    states: { valueStr, value: writablePin }
  } = createPinInput({
    defaultValue: []
  });

  let login = null;
  let password = null;
  let recoveryCode: string;
  let isLoggingIn = false;
  // The below variables are all related to the OTP flow
  let isOtpRequired = false;
  let isRecoverRequired = false;
  let isAccountLocked = false;
  let isOAuthOtpFlow = false;
  let otpRecordId: string;
  let otpProvider: string;

  // This function exists to handle the OTP flow when using OAuth login
  async function handleOAuthLogin() {
    // We're just going to re-use the existing OAuth flow which is based on $page, so we
    // create a similar structure so the execution happens, but all it really cares about
    // is the query parameters.
    const url = new URL('https://not-actually-real.com');
    if ($valueStr.length > 0) {
      url.searchParams.set('otp', $valueStr);
    } else if (recoveryCode) {
      url.searchParams.set('otpRecoveryCode', recoveryCode);
    }
    url.searchParams.set('otpRecordId', otpRecordId);
    url.searchParams.set('code', 'not-actually-a-code');
    const response = await authenticateWithAPI({
      params: { provider: `${otpProvider}/login` },
      url
    });
    // Handle the various otp failure states
    if (response.data.code === 'otp_invalid_code') {
      return Toasts.push({
        kind: 'error',
        content: response.data.content
      });
    } else if (response.data.code === 'otp_locked_out') {
      isOtpRequired = false;
      isRecoverRequired = true;
      isAccountLocked = true;
      writablePin.set([]);
      return;
    } else if (response.data.code === 'otp_invalid_recovery_code') {
      return Toasts.push({
        kind: 'error',
        content: response.data.content
      });
    } else if (response.status === 'success') {
      // On success, refresh the session and dispatch to the next step in the flow
      await refreshSession(response.data.accessToken, response.data.refreshToken);
      analytics.track({ name: 'Logged In', props: { method: 'twitch' } });
      if ($irkAccount!.name === '') {
        dispatch('needsUsername');
      } else {
        dispatch('done');
      }
      return;
    } else {
      // This shouldn't ever really happen...
      return Toasts.push({
        kind: 'error',
        content: 'There was an error authenticating your account'
      });
    }
  }

  async function onLogin() {
    try {
      isLoggingIn = true;
      // If we're attempting Login then handle it and skip the rest of the function
      // body
      if (isOAuthOtpFlow) {
        return await handleOAuthLogin();
      }

      const response = await fetch(`${BASE_APP_URL}/auth/proxy/login.json`, {
        method: 'POST',
        body: JSON.stringify({
          login,
          password,
          otp: $valueStr.length === 0 ? undefined : $valueStr,
          otpRecoveryCode: recoveryCode
        })
      });
      if (!response) {
        throw new Error();
      }

      const json = await response.json();
      if (response.status >= 400) {
        if (json.code === 'otp_required') {
          isOtpRequired = true;
          return;
        } else if (json.code === 'otp_invalid_code') {
          return Toasts.push({
            kind: 'error',
            content: 'The authentication code you provided was invalid.'
          });
        } else if (json.code === 'otp_locked_out') {
          isOtpRequired = false;
          isRecoverRequired = true;
          isAccountLocked = true;
          writablePin.set([]);
          return;
        } else if (json.code === 'otp_invalid_recovery_code') {
          return Toasts.push({
            kind: 'error',
            content: 'The recovery code you provided was invalid.'
          });
        } else {
          throw new Error();
        }
      }

      // We're in! 🚀
      await refreshSession(json.accessToken, json.refreshToken, '/auth/account/login');

      // Analytics
      analytics.track({ name: 'Logged In', props: { method: 'metafy' } });

      if ($irkAccount!.name === '') {
        dispatch('needsUsername');
      } else {
        dispatch('done');
      }
    } catch (error) {
      Toasts.push({
        kind: 'error',
        content: 'There was an issue logging you in. Is your email & password correct?'
      });
    } finally {
      isLoggingIn = false;
    }
  }

  function validate() {
    if (!login) {
      return 'You must fill in your email address.';
    }
    if (!password) {
      return 'You must fill in your password.';
    }
    if (password.length < 6) {
      return 'Your password must be longer than 6 characters.';
    }
  }

  function handleSubmit(e) {
    const error = validate();
    if (error) {
      e.preventDefault();
      return Toasts.push({
        kind: 'error',
        content: error
      });
    }
  }
</script>

<div class="{className} mx-auto flex w-full flex-col xl:my-auto xl:mt-auto xl:flex-row">
  {#if variant === 'full'}
    <!-- <div
      class="illustration-bg relative mx-auto w-full rounded-l-2xl bg-contain xl:h-auto xl:basis-1/2"
      style="--illustration-url: url('{STATIC_URL}/illustrations/astronaut_new_bg.svg');"
    >
      <Image
        src="{STATIC_URL}/illustrations/astronaut_new.svg"
        size="w-full h-[160px] md:h-[300px] xl:h-full"
        class="pointer-events-none mx-auto mb-5 md:mb-8 xl:mb-0"
      />
    </div> -->
    <div
      class="illustration-bg relative rounded-3xl xl:w-1/2"
      style="--illustration-url: url('{STATIC_URL}/illustrations/astronaut_new_bg.svg');"
    >
      <SpaceManAnimation
        class="relative -left-[5%] m-auto max-h-40 max-w-64 md:max-h-none xl:-left-[10%] xl:-top-[10%] xl:max-h-[120%] xl:max-w-none"
      />
    </div>
  {/if}

  <div class="{variant === 'full' ? 'xl:basis-1/2' : ''} px-6 xl:my-auto xl:px-10 xl:pt-6">
    {#if isOtpRequired}
      {#if variant === 'full'}
        <h3 class="text-display-sm text-ondark-primary mb-2 text-center xl:text-left">Authentication Code</h3>
        <p class="text-ondark-secondary mb-6 text-sm">
          To continue, please enter the 6-digit verification code generated by your authenticator app.
        </p>
      {/if}
      <form on:submit|preventDefault={onLogin} class="flex flex-col items-center">
        <div {...$root} use:root class="mb-6 flex items-center gap-2">
          {#each Array.from({ length: 6 }) as _, idx}
            <input
              class="input size-12 text-center"
              {...$input()}
              use:input
              {...idx === 0 ? { autofocus: true } : {}}
            />
          {/each}
        </div>
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <p class="text-ondark-secondary text-body-3xs mb-6">
          If you've lost your authenticator, you can recover your account <span
            class="text-action-ondark-base cursor-pointer"
            on:click={() => {
              isOtpRequired = false;
              isRecoverRequired = true;
              isAccountLocked = false;
              writablePin.set([]);
            }}
          >
            here
          </span>.
        </p>
        <Button
          width="full"
          size="lg"
          type="submit"
          icon={faArrowRight}
          iconPosition="right"
          loading={isLoggingIn}
          disabled={$valueStr.length !== 6}
        >
          Continue
        </Button>
      </form>
    {:else if isRecoverRequired}
      {#if variant === 'full'}
        <h3 class="text-display-sm text-ondark-primary mb-2 text-center xl:text-left">Recover Your Account</h3>
        {#if isAccountLocked}
          <p class="text-ondark-secondary mb-4 text-sm">
            Your account has been locked from too many failed login attempts.
          </p>
        {/if}
        <p class="text-ondark-secondary mb-6 text-sm">
          You must use one of your recovery codes to login. If you no longer have your recovery codes, please contact
          support.
        </p>
        <form on:submit|preventDefault={onLogin} class="flex flex-col">
          <FormControl
            class="mb-6 w-full"
            type="text"
            placeholder="Recovery code"
            on:input={event => (recoveryCode = event.target.value)}
          />
          <Button
            width="full"
            size="lg"
            type="submit"
            icon={faArrowRight}
            iconPosition="right"
            loading={isLoggingIn}
            disabled={!recoveryCode}
          >
            Continue
          </Button>
        </form>
      {/if}
    {:else}
      {#if variant === 'full'}
        <h3 class="text-display-sm text-ondark-primary mb-6 text-center xl:text-left">Login</h3>
      {/if}
      <OAuthButtons
        type="login"
        on:after={() => dispatch('done')}
        on:needsOtp={({ detail }) => {
          isOtpRequired = true;
          isOAuthOtpFlow = true;
          otpRecordId = detail.recordId;
          otpProvider = detail.provider;
        }}
      />

      <hr class="border-ondark-subtle my-6" />

      <form on:submit|preventDefault={onLogin} class="flex flex-col">
        <div class="mb-6 flex flex-col">
          <div>
            <FormControl
              id="email"
              type="email"
              placeholder="Email Address"
              on:input={event => (login = event.target.value)}
            />
          </div>

          <div class="mt-5">
            <FormControl
              id="password"
              type="password"
              inlinePadding={52}
              placeholder="Password"
              actionLink="/auth/account/forgot-password"
              on:input={event => (password = event.target.value)}
            />
          </div>

          <a
            class="tracking-0.005 text-action-ondark-base ml-auto mt-3 text-sm font-medium leading-none transition-colors hover:underline"
            href="/auth/account/forgot-password"
          >
            Forgot Password?
          </a>
        </div>

        <Button
          size="lg"
          type="submit"
          icon={faArrowRight}
          iconPosition="right"
          loading={isLoggingIn}
          on:click={handleSubmit}
        >
          Login
        </Button>

        <!-- Switch to "Create account" -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <svelte:element
          this={switchHref ? 'a' : 'button'}
          href={switchHref ? '/auth/account/create' + $page.url.search : null}
          role={switchHref ? 'link' : 'button'}
          on:click={() => {
            if (!switchHref) {
              dispatch('switch');
            }
          }}
          class="text-ondark-primary mt-5 text-left font-medium hover:underline"
        >
          Don't have an account?
          <span class="text-action-ondark-base">Create one</span>
        </svelte:element>
      </form>
    {/if}
  </div>
</div>

<style>
  .illustration-bg {
    @screen xl {
      background-image: var(--illustration-url);
    }
  }
</style>
